import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { SearchPicker } from '../SearchPicker';

// "Create trade-in quote" → Draft Sales Order with the Lyphline product + negative credit line.
export const TradeInQuoteButton = ({ lead }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [pid, setPid] = useState('');
  const [credit, setCredit] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api().get('/product-types?level=product').then(r => setProducts((r.data || []).filter(p => p.active_status !== false))).catch(() => {});
    api().get(`/trade-in-leads/${encodeURIComponent(lead.key)}/quote-defaults`).then(r => setDesc(r.data.credit_description || '')).catch(() => {});
  }, [open, api, lead.key]);

  const p = products.find(x => x.id === pid);
  const total = (Number(p?.sales_price || 0) - Number(credit || 0));
  const create = async () => {
    if (!pid) { toast.error('Pick the Lyphline product'); return; }
    setBusy(true);
    try {
      const r = await api().post(`/trade-in-leads/${encodeURIComponent(lead.key)}/quote`, { product_type_id: pid, credit_amount: Number(credit || 0), credit_description: desc });
      toast.success(`${r.data.so_number} created`);
      navigate(`/orders/sales/${r.data.so_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create quote'); } finally { setBusy(false); }
  };

  if (lead.quote_so_id) {
    return <Button size="sm" variant="outline" className="h-8 rounded-sm" onClick={() => navigate(`/orders/sales/${lead.quote_so_id}`)} data-testid={`lead-quote-link-${lead.key}`}><FileText className="w-3.5 h-3.5 mr-1" />{lead.quote_so_number}</Button>;
  }
  return (
    <>
      <Button size="sm" variant="outline" className="h-8 rounded-sm border-red-300 text-red-700 hover:bg-red-50" onClick={() => setOpen(true)} data-testid={`lead-quote-btn-${lead.key}`}><FileText className="w-3.5 h-3.5 mr-1" />Trade-in quote</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="trade-in-quote-dialog">
          <DialogHeader>
            <DialogTitle>Trade-in quote for {lead.customer || 'this customer'}</DialogTitle>
            <DialogDescription>Creates a Draft sales order with the Lyphline dryer and a trade-in credit line. You can edit it before issuing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Lyphline product *</Label>
              <SearchPicker value={pid} onChange={(v) => setPid(v || '')} placeholder="— pick a product —" testId="trade-in-product"
                options={products.map(x => ({ value: x.id, label: x.product_sku ? `${x.product_sku} — ${x.product_type_name}` : x.product_type_name, sub: x.sales_price != null ? `$${Number(x.sales_price).toFixed(2)}` : 'no price set' }))} />
            </div>
            <div className="space-y-1"><Label>Trade-in credit ($)</Label><Input type="number" min="0" step="0.01" value={credit} onChange={(e) => setCredit(e.target.value)} placeholder="0.00" className="rounded-sm mono" data-testid="trade-in-credit" /></div>
            <div className="space-y-1"><Label>Credit line description</Label><Input value={desc} maxLength={300} onChange={(e) => setDesc(e.target.value)} className="rounded-sm text-xs" data-testid="trade-in-desc" /></div>
            {p && <p className="text-sm" data-testid="trade-in-preview">Dryer ${Number(p.sales_price || 0).toFixed(2)} − credit ${Number(credit || 0).toFixed(2)} = <b>${total.toFixed(2)}</b> before tax</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy || !pid} className="bg-red-600 hover:bg-red-700" data-testid="trade-in-create-btn">{busy ? 'Creating…' : 'Create draft quote'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
